import { useState, useEffect, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Lightbulb, CheckCircle, X, Eye, ArrowsClockwise, CloudArrowDown, GitMerge } from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { da } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const styles = { 
    pending: "bg-amber-50 text-amber-700 border-amber-200", 
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200", 
    ignored: "bg-slate-100 text-slate-600 border-slate-200",
    merged: "bg-blue-50 text-blue-700 border-blue-200"
  };
  const labels = { pending: "Afventer", approved: "Godkendt", ignored: "Ignoreret", merged: "Flettet" };
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${styles[status] || styles.pending}`}>{labels[status] || status}</span>;
};

const JobStatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-slate-100 text-slate-700 border-slate-200",
    running: "bg-blue-50 text-blue-700 border-blue-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed: "bg-red-50 text-red-700 border-red-200"
  };
  const labels = { pending: "Venter", running: "Kører", completed: "Færdig", failed: "Fejlet" };
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${styles[status] || styles.pending}`}>{labels[status] || status}</span>;
};

export const SuggestionsPage = ({ user, setUser }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [sites, setSites] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSite, setFilterSite] = useState("all");
  const [filterStatus, setFilterStatus] = useState("pending");
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState("");
  const [importJob, setImportJob] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => { fetchSuggestions(); fetchSites(); fetchArticles(); }, [filterSite, filterStatus]);

  // Poll for import job status
  useEffect(() => {
    if (!importJob || importJob.status === 'completed' || importJob.status === 'failed') return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API}/zendesk/import-status/${importJob.job_id}`, { credentials: 'include' });
        if (response.ok) {
          const jobData = await response.json();
          setImportJob(jobData);
          
          if (jobData.status === 'completed') {
            toast.success(`Import færdig! ${jobData.suggestions_created} nye forslag oprettet.`);
            fetchSuggestions();
          } else if (jobData.status === 'failed') {
            toast.error(`Import fejlede: ${jobData.error}`);
          }
        }
      } catch (error) {
        console.error('Failed to poll job status:', error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [importJob?.job_id, importJob?.status]);

  const fetchSuggestions = async () => {
    try {
      const params = new URLSearchParams();
      if (filterSite !== 'all') params.append('site_key', filterSite);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const response = await fetch(`${API}/zendesk/suggestions?${params}`, { credentials: 'include' });
      if (response.ok) setSuggestions(await response.json());
    } catch (error) { toast.error("Kunne ikke hente forslag"); }
    finally { setLoading(false); }
  };

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API}/sites`, { credentials: 'include' });
      if (response.ok) setSites(await response.json());
    } catch (error) { console.error('Failed to fetch sites:', error); }
  };

  const fetchArticles = async () => {
    try {
      const response = await fetch(`${API}/knowledge-articles?status=approved`, { credentials: 'include' });
      if (response.ok) setArticles(await response.json());
    } catch (error) { console.error('Failed to fetch articles:', error); }
  };

  const startImport = async () => {
    setImporting(true);
    try {
      const response = await fetch(`${API}/zendesk/import-tickets?days=30&max_tickets=50`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.success) {
        setImportJob({ job_id: data.job_id, status: 'pending', progress: 0, total_tickets: 0 });
        toast.success("Import startet i baggrunden");
      } else if (data.status === 'running') {
        setImportJob(data);
        toast.info("Der kører allerede et import job");
      } else {
        toast.error(data.message || "Kunne ikke starte import");
      }
    } catch (error) {
      toast.error("Der opstod en fejl ved start af import");
    } finally {
      setImporting(false);
    }
  };

  const handleApprove = async (suggestionId) => {
    try {
      const response = await fetch(`${API}/zendesk/suggestions/${suggestionId}/approve`, { method: 'POST', credentials: 'include' });
      if (response.ok) { toast.success("Forslag godkendt - ny artikel oprettet"); fetchSuggestions(); setPreviewOpen(false); }
      else { const error = await response.json(); toast.error(error.detail || "Kunne ikke godkende"); }
    } catch (error) { toast.error("Der opstod en fejl"); }
  };

  const handleIgnore = async (suggestionId) => {
    try {
      const response = await fetch(`${API}/zendesk/suggestions/${suggestionId}/ignore`, { method: 'POST', credentials: 'include' });
      if (response.ok) { toast.success("Forslag ignoreret"); fetchSuggestions(); setPreviewOpen(false); }
      else { const error = await response.json(); toast.error(error.detail || "Kunne ikke ignorere"); }
    } catch (error) { toast.error("Der opstod en fejl"); }
  };

  const handleMerge = async () => {
    if (!selectedSuggestion || !selectedArticle) {
      toast.error("Vælg en artikel at flette ind i");
      return;
    }
    try {
      const response = await fetch(`${API}/zendesk/suggestions/${selectedSuggestion.suggestion_id}/merge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_article_id: selectedArticle })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Indhold flettet ind i artikel (version ${data.new_version})`);
        fetchSuggestions();
        setMergeOpen(false);
        setPreviewOpen(false);
        setSelectedArticle("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Kunne ikke flette");
      }
    } catch (error) {
      toast.error("Der opstod en fejl");
    }
  };

  const formatDate = (dateStr) => { if (!dateStr) return "-"; try { return format(new Date(dateStr), "d. MMM yyyy HH:mm", { locale: da }); } catch { return "-"; } };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 md:p-8" data-testid="suggestions-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Zendesk Forslag</h1>
            <p className="text-slate-600">Gennemgå AI-genererede forslag baseret på Zendesk tickets</p>
          </div>
          <div className="flex gap-3 items-center">
            <Select value={filterSite} onValueChange={setFilterSite}>
              <SelectTrigger className="w-[160px]" data-testid="filter-site"><SelectValue placeholder="Alle sites" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Alle sites</SelectItem>{sites.map(site => <SelectItem key={site.site_key} value={site.site_key}>{site.brand_name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Alle status</SelectItem><SelectItem value="pending">Afventer</SelectItem><SelectItem value="approved">Godkendt</SelectItem><SelectItem value="ignored">Ignoreret</SelectItem><SelectItem value="merged">Flettet</SelectItem></SelectContent>
            </Select>
            <Button 
              onClick={startImport} 
              disabled={importing || (importJob?.status === 'running')}
              className="btn-primary"
              data-testid="import-btn"
            >
              {importing || importJob?.status === 'running' ? (
                <><ArrowsClockwise className="w-4 h-4 mr-2 animate-spin" />Importerer...</>
              ) : (
                <><CloudArrowDown className="w-4 h-4 mr-2" />Importer fra Zendesk</>
              )}
            </Button>
          </div>
        </div>

        {/* Import Job Status Card */}
        {importJob && importJob.status !== 'completed' && importJob.status !== 'failed' && (
          <Card className="card-default border-blue-200 bg-blue-50/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ArrowsClockwise className="w-5 h-5 text-blue-600 animate-spin" />
                  <span className="font-medium text-slate-900">Import kører...</span>
                  <JobStatusBadge status={importJob.status} />
                </div>
                <span className="text-sm text-slate-600">
                  {importJob.progress || 0} / {importJob.total_tickets || '?'} tickets
                </span>
              </div>
              {importJob.total_tickets > 0 && (
                <Progress value={(importJob.progress / importJob.total_tickets) * 100} className="h-2" />
              )}
            </CardContent>
          </Card>
        )}

        {/* Import Completed Card */}
        {importJob && importJob.status === 'completed' && (
          <Card className="card-default border-emerald-200 bg-emerald-50/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600" weight="fill" />
                <div className="flex-1">
                  <p className="text-sm text-slate-700">
                    <strong>Import færdig!</strong> {importJob.tickets_processed} tickets behandlet, {importJob.suggestions_created} nye forslag oprettet.
                    {importJob.tickets_skipped_irrelevant > 0 && ` ${importJob.tickets_skipped_irrelevant} irrelevante skippet.`}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setImportJob(null)}><X className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="card-default border-indigo-200 bg-indigo-50/30 mb-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" weight="duotone" />
              <p className="text-sm text-slate-700"><strong>Tip:</strong> Klik "Importer fra Zendesk" for at hente løste tickets og generere KB forslag automatisk. Importen kører i baggrunden.</p>
            </div>
          </CardContent>
        </Card>

        {loading ? <div className="space-y-4">{[1, 2, 3].map(i => <Card key={i} className="card-default animate-pulse"><CardContent className="p-6"><div className="h-6 bg-slate-200 rounded w-3/4 mb-4" /><div className="h-4 bg-slate-100 rounded w-1/2" /></CardContent></Card>)}</div>
        : suggestions.length === 0 ? (
          <Card className="card-default"><CardContent className="p-12 text-center"><Lightbulb className="w-12 h-12 text-slate-300 mx-auto mb-4" weight="duotone" /><h3 className="text-lg font-semibold text-slate-900 mb-2">Ingen forslag endnu</h3><p className="text-slate-600 max-w-md mx-auto">Når Zendesk integration er konfigureret, vil AI-genererede forslag vises her.</p></CardContent></Card>
        ) : (
          <div className="space-y-4">{suggestions.map((suggestion, index) => (
            <motion.div key={suggestion.suggestion_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
              <Card className="card-default hover:shadow-md transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2"><Badge variant="outline" className="text-xs">{suggestion.suggested_action === 'new_article' ? 'Ny artikel' : 'Tilføj'}</Badge><StatusBadge status={suggestion.status} /></div>
                      <h3 className="font-semibold text-slate-900 mb-1">{suggestion.suggested_title || "Forslag"}</h3>
                      <p className="text-sm text-slate-600 line-clamp-2">{suggestion.suggested_content}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-500"><span>Site: {suggestion.site_key}</span><span>Oprettet: {formatDate(suggestion.created_at)}</span></div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button variant="outline" size="sm" onClick={() => { setSelectedSuggestion(suggestion); setPreviewOpen(true); }} data-testid={`preview-${suggestion.suggestion_id}`}><Eye className="w-4 h-4 mr-1" />Se</Button>
                      {suggestion.status === 'pending' && <>
                        <Button variant="outline" size="sm" className="text-emerald-600 hover:bg-emerald-50" onClick={() => handleApprove(suggestion.suggestion_id)} data-testid={`approve-${suggestion.suggestion_id}`}><CheckCircle className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" className="text-blue-600 hover:bg-blue-50" onClick={() => { setSelectedSuggestion(suggestion); setMergeOpen(true); }} data-testid={`merge-${suggestion.suggestion_id}`}><GitMerge className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" onClick={() => handleIgnore(suggestion.suggestion_id)} data-testid={`ignore-${suggestion.suggestion_id}`}><X className="w-4 h-4" /></Button>
                      </>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}</div>
        )}

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Forhåndsvisning</DialogTitle><DialogDescription>Se detaljer om forslaget</DialogDescription></DialogHeader>
            {selectedSuggestion && <div className="space-y-4 py-4">
              <div className="flex items-center gap-2"><Badge variant="outline">{selectedSuggestion.suggested_action === 'new_article' ? 'Ny artikel' : 'Tilføj'}</Badge><StatusBadge status={selectedSuggestion.status} /></div>
              <div><h4 className="text-sm font-medium text-slate-500 mb-1">Titel</h4><p className="text-lg font-semibold">{selectedSuggestion.suggested_title || "Ingen"}</p></div>
              <div><h4 className="text-sm font-medium text-slate-500 mb-1">Indhold</h4><div className="p-4 bg-slate-50 rounded-lg"><p className="whitespace-pre-wrap">{selectedSuggestion.suggested_content}</p></div></div>
            </div>}
            <DialogFooter>{selectedSuggestion?.status === 'pending' ? <>
              <Button variant="outline" onClick={() => handleIgnore(selectedSuggestion.suggestion_id)}><X className="w-4 h-4 mr-2" />Ignorer</Button>
              <Button variant="outline" className="text-blue-600" onClick={() => { setPreviewOpen(false); setMergeOpen(true); }}><GitMerge className="w-4 h-4 mr-2" />Flet</Button>
              <Button className="btn-primary" onClick={() => handleApprove(selectedSuggestion.suggestion_id)}><CheckCircle className="w-4 h-4 mr-2" />Godkend</Button>
            </> : <Button variant="outline" onClick={() => setPreviewOpen(false)}>Luk</Button>}</DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Merge Dialog */}
        <Dialog open={mergeOpen} onOpenChange={(open) => { setMergeOpen(open); if (!open) setSelectedArticle(""); }}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Flet ind i eksisterende artikel</DialogTitle>
              <DialogDescription>Vælg den artikel du vil tilføje indholdet til. Indholdet tilføjes i bunden af artiklen.</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Forslag: {selectedSuggestion?.suggested_title}</h4>
                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600 max-h-32 overflow-y-auto">
                  {selectedSuggestion?.suggested_content}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Vælg målartkel</label>
                <Select value={selectedArticle} onValueChange={setSelectedArticle}>
                  <SelectTrigger data-testid="merge-article-select"><SelectValue placeholder="Vælg artikel..." /></SelectTrigger>
                  <SelectContent>
                    {articles.map(article => (
                      <SelectItem key={article.article_id} value={article.article_id}>
                        {article.title} ({article.category})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setMergeOpen(false); setSelectedArticle(""); }}>Annuller</Button>
              <Button className="btn-primary" onClick={handleMerge} disabled={!selectedArticle} data-testid="confirm-merge-btn">
                <GitMerge className="w-4 h-4 mr-2" />Flet ind
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
